class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      const variantId =
        this.querySelector('[data-variant-id]')?.dataset.variantId ||
        this.querySelector('button')?.dataset.variantId;

      cartItems.updateQuantity(this.dataset.index, 0, 'remove', variantId);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  get immediateQuantityUpdates() {
    return false;
  }

  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const onQuantityChange = this.immediateQuantityUpdates
      ? (event) => this.onChange(event)
      : debounce((event) => {
          this.onChange(event);
        }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', onQuantityChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      // The add/update payload already includes section HTML. Refetching
      // cart-drawer races that paint and 5xxs on this store (AAS-5377).
      if (this.tagName === 'CART-DRAWER-ITEMS' && event.cartData?.sections?.['cart-drawer']) {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name'),
      event.target.dataset.quantityVariantId
    );
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'main-cart-shipping',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '#main-cart-shipping',
      },
      {
        id: 'main-cart-discount',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '#main-cart-discount',
      },
      {
        id: 'main-cart-total',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '#main-cart-total',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      }/*,
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      }*/
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartOrder = document.querySelector('.cart-order ');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartOrder) cartOrder.classList.toggle('is-empty', parsedState.item_count === 0);

        const cartPage = document.querySelector('.cart-page');
        if (cartPage) cartPage.classList.toggle('is-empty', parsedState.item_count === 0);

        const countBadge = document.querySelector('.cart-page__count-badge');
        const countBadgeValue = countBadge?.querySelector('span');
        if (countBadge && countBadgeValue) {
          countBadgeValue.textContent = parsedState.item_count;
          countBadge.hidden = parsedState.item_count === 0;
        }

        /* Shopify can answer a cart AJAX request with `sections: null` on an
           otherwise successful 200 (see assets/cart-drawer.js renderContents).
           Reading sections[...] off that null throws and takes down the whole
           update, so skip the repaint — the cart mutation already succeeded.
           The `if (!html)` guard below is too late: the dereference is first. */
        if (!parsedState.sections) return;

        this.getSectionsToRender().forEach((section) => {
          const container = document.getElementById(section.id);
          if (!container) {
            return; 
          }
        
          const elementToReplace = container.querySelector(section.selector) || container;
          const html = parsedState.sections[section.section];
        
          if (!html) {
            return;
          }
        
          const newInner = this.getSectionInnerHTML(html, section.selector);
          if (!newInner) {
            return;
          }

          // Re-use already-decoded line item images so thumbnails don't blink
          // when the cart contents are re-rendered after a quantity change.
          if (section.selector === '.js-contents' || section.selector === '.drawer__contents') {
            this.replaceContentsPreservingMedia(elementToReplace, newInner);
          } else {
            elementToReplace.innerHTML = newInner;
          }
        });
        
        
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));

        const toast = document.querySelector('cart-error-toast');
        if (toast && typeof toast.show === 'function') {
          toast.show(window.cartStrings.error);
          return;
        }

        const errors = document.getElementById('cart-errors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });

      const cartUpdatedEvent = new CustomEvent('cartUpdated', {
        detail: {
          message: 'Cart was updated',
        }
      });
      document.dispatchEvent(cartUpdatedEvent);
      
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    if (!html) return null;
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : null;
  }

  replaceContentsPreservingMedia(container, newInnerHTML) {
    const parsed = new DOMParser().parseFromString(newInnerHTML, 'text/html');

    const oldItems = new Map();
    container.querySelectorAll('.cart-item').forEach((el) => {
      if (el.id) oldItems.set(el.id, el);
    });

    const newItems = [...parsed.body.querySelectorAll('.cart-item')];
    const newIds = newItems.map((el) => el.id).filter(Boolean);

    // Only morph in place when the exact same set of line items is present
    // (e.g. a quantity change). If items were added/removed, fall back to a
    // full replace so ordering and structure stay correct.
    const sameItemSet =
      oldItems.size === newItems.length &&
      newIds.length === newItems.length &&
      newIds.every((id) => oldItems.has(id));

    if (!sameItemSet) {
      container.innerHTML = newInnerHTML;
      return;
    }

    newItems.forEach((newItem) => {
      const oldItem = oldItems.get(newItem.id);
      if (!oldItem) return;

      // Update only the text/price/quantity column; leave .cart-item__media
      // (the thumbnail) in the DOM untouched so the image never repaints.
      const oldInfo = oldItem.querySelector('.cart-item__infor');
      const newInfo = newItem.querySelector('.cart-item__infor');
      if (oldInfo && newInfo && oldInfo.innerHTML !== newInfo.innerHTML) {
        oldInfo.innerHTML = newInfo.innerHTML;
      }

      // Refresh the thumbnail only when the image source actually changed.
      const oldImg = oldItem.querySelector('.cart-item__media img');
      const newImg = newItem.querySelector('.cart-item__media img');
      if (oldImg && newImg && oldImg.getAttribute('src') !== newImg.getAttribute('src')) {
        const oldMedia = oldItem.querySelector('.cart-item__media');
        const newMedia = newItem.querySelector('.cart-item__media');
        if (oldMedia && newMedia) oldMedia.innerHTML = newMedia.innerHTML;
      }
    });
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
